import React from 'react';
import { Redirect, Prompt } from 'react-router-dom';
import ModalEnabled from '../extend/ModalEnabled';
import { chefAPI } from '../../InterfaceAPI/chefAPI';
import DetailsBar from '../shared/DetailsBar';
import HeadingPlaceholder from '../shared/HeadingPlaceholder';
import ChefServices from './ChefServices';
import ChefEditDetailsModal from './ChefEditDetailsModal';
import ChefDeclineOpportunityModal from './ChefDeclineOpportunityModal';
import AccountStatus from '../shared/AccountStatus';
import ContactInformationBar from '../shared/ContactInformationBar';
import withSplashMessage from '../splashMessage/withSplashMessage';
import { stringfyServerErrors } from '../../helpers/util';
import withUser from '../auth/withUser';
import {
  maskCapitalizeFirstLetter,
  maskDate,
  maskMoney,
  maskServiceEndTime,
  maskServiceStartTime
} from '../../masks';
import ChefAvailabilitySection from './ChefAvailabilitySection';
import ChefTimeOffModal from './ChefTimeOffModal';
import ChefLocationModal from './ChefLocationModal';
import ConfirmModal from '../shared/ConfirmModal';
import ENV_CONFIG from '../../env';
import { authAPI } from '../../InterfaceAPI/authAPI';
import withTracker from '../withTracker';
import Loader from '../shared/Loader';
import {
  createZipMap,
  maskServiceDistricts
} from '../../helpers/serviceDistrictHelpers.js';
import { get as _get } from 'lodash'
import { ACCOUNT_STATUSES } from '../../const.js';

class ChefProfile extends ModalEnabled {
  defaultChefState = {
    service_addresses: []
  };

  state = {
    chef: { ...this.defaultChefState },
    availableServiceDistricts: [],
    addressIndex: -1,
    timeOffIndex: -1,
    opportunityIndex: -1,
    unsaved: false,
    redirect_to: '',
    isLoading: true,
    zipMap: {}
  };

  detailsBarProperties = [
    {
      label: 'Available For',
      key: 'occasion_opt_in',
      mask: this.maskAvailability
    },
    {
      label: 'Locations',
      key: 'service_districts',
      mask: maskServiceDistricts
    },
    {
      label: 'Total Services This Month',
      combine: [
        { dive: 'this_month', key: 'services_stats' },
        {
          dive: 'this_month_completed',
          key: 'services_stats',
          mask: this.maskCompleted
        }
      ]
    },
    { label: 'Total Services Completed', dive: 'total', key: 'services_stats' },
    { label: 'Service Rate', key: 'tier', mask: maskMoney }
  ];

  chefServiceListColumns = [
    { label: 'Date', key: 'date', sortable: false, mask: maskDate },
    {
      label: 'Time',
      sortable: false,
      combine: [
        { key: 'start_time', mask: maskServiceStartTime },
        { key: 'end_time', mask: maskServiceEndTime }
      ]
    },
    { label: 'Client', key: 'client', sortable: false, dive: 'name' },
    {
      label: 'Location',
      key: 'location'
    },
    {
      label: 'Service Type',
      key: 'service_type',
      sortable: false,
      mask: maskCapitalizeFirstLetter
    },
    {
      label: 'Rate',
      key: 'invited_chefs',
      mask: (invitedChefs) => {
        const currentChefInvite = (invitedChefs || []).find(invite => 
          _get(invite, 'chef.id') === _get(this, 'state.chef.id')
        );
        return currentChefInvite ? maskMoney(currentChefInvite.rate) : ""
      }
    },
    { link: { to: '/service/[id]', label: 'View' }, sortable: false }
  ];

  render() {
    const {
      chef,
      redirect_to,
      unsaved,
      isLoading,
      timeOffIndex,
      zipMap
    } = this.state;
    const chefName = `${chef.first_name || ''} ${chef.last_name || ''}`;

    const isActive = chef.account_status !== ACCOUNT_STATUSES.DEACTIVATED;
    if (redirect_to) return <Redirect to={redirect_to} />;

    if (isLoading) return <Loader />;

    const opportunity =
      this.state.opportunityIndex >= 0
        ? chef.service_invited_chefs[this.state.opportunityIndex]
        : null;

    return (
      <div className="cms cms--public">
        <div className="cms__head cms__head--padding">
          <div className="cms__content">
            {/*<BackButton defaultUrl='/admin/chefs'/>*/}
            {chef.id && (
              <AccountStatus
                className="p--blue"
                accountType="Chef"
                showAccountType={false}
                accountStatus={chef.account_status}
                date={isActive ? chef.created_at : chef.deactivation_date}
              />
            )}
            <div className="cms__bar cms__bar--wrap">
              <div className="cms__user-heading">
                <HeadingPlaceholder
                  value={chefName === ' ' ? null : chefName}
                  placeholder="Chef"
                />
                <ContactInformationBar
                  phone={chef.phone_number}
                  email={chef.email}
                  unconfirmed_email={chef.unconfirmed_email}
                />
              </div>
              <div className="cms__bar-ctas">
                <div
                  className="cms__bar-cta btn btn--link btn--full-mobile"
                  onClick={() => this.openModal('reset-password')}
                >
                  Reset My Password
                </div>
                <div
                  className="cms__bar-cta btn btn--hollow-blue btn--full-mobile"
                  onClick={() => this.openModal('edit-details')}
                >
                  Edit Details
                </div>
                <input
                  type="submit"
                  className="cms__bar-cta btn btn--solid-blue btn--full-mobile"
                  onClick={this.handleSubmit}
                  value="Save"
                />
              </div>
            </div>
            <DetailsBar data={chef} columns={this.detailsBarProperties} />
          </div>
        </div>

        <div className="cms__body">
          <div className="cms__content">
            <div className="row">
              <ChefServices
                chef={chef}
                updateChef={this.updateChef}
                title="Booked Services"
                columns={this.chefServiceListColumns}
                zipMap={zipMap}
              />
            </div>

            {/* Hide this for now */}
            {/* <div className="row">
              <ChefOpportunities
                opportunities={chef.service_invited_chefs}
                chefId={chef.id}
                openDeclineModal={more =>
                  this.openModal('decline-service', more)
                }
              />
            </div> */}

            <div className="row">
              <div className="row__item row__item--two-third row__item--pad-right">
                <ChefAvailabilitySection
                  chef={chef}
                  availabilities={chef.chef_availabilities}
                  timeOff={chef.chef_time_offs}
                  updateChef={this.updateChef}
                  revertAvailability={this.revertAvailability}
                  openModal={more => this.openModal('edit-time-off', more)}
                  isMobile={this.props.isMobile}
                />
              </div>
              <div className="row__item row__item--third row__item--pad-left">
                {/* Hide this for now */}
                {/* <ChefLocation
                  openModal={() => this.openModal('edit-locations')}
                  serviceDistricts={chef.service_district_subsections}
                  availableServiceDistricts={
                    this.state.availableServiceDistricts
                  }
                /> */}
              </div>
            </div>
          </div>
        </div>

        <ChefTimeOffModal
          isOpen={this.isOpen('edit-time-off')}
          closeModal={() => this.closeModal({ timeOffIndex: -1 })}
          index={timeOffIndex}
          chef_time_offs={chef.chef_time_offs}
          updateChef={this.updateChef}
        />

        <ChefEditDetailsModal
          isOpen={this.isOpen('edit-details')}
          closeModal={this.closeModal}
          isRoleAdmin={false}
          chef={chef}
          updateChef={this.updateChef}
          availableServiceDistricts={this.state.availableServiceDistricts}
          serviceDistricts={chef.service_district_subsections}
        />

        <ChefLocationModal
          isOpen={this.isOpen('edit-locations')}
          closeModal={this.closeModal}
          availableServiceDistricts={this.state.availableServiceDistricts}
          serviceDistricts={chef.service_district_subsections}
          updateChef={this.updateChef}
        />

        <ChefDeclineOpportunityModal
          isOpen={this.isOpen('decline-service')}
          closeModal={() => this.closeModal({ opportunityIndex: -1 })}
          opportunity={opportunity}
          onSubmit={this.handleDeclineService}
        />

        <ConfirmModal
          isOpen={this.isOpen('reset-password')}
          closeModal={this.closeModal}
          onSubmit={this.handleResetPassword}
          heading="Reset Password"
          body="This will send an email to reset the password. Are you sure?"
          cta="Okay"
        />

        <Prompt when={unsaved} message="You have unsaved data, are you sure?" />
      </div>
    );
  }

  updateChef = updatedChef => {
    const chef = {
      ...this.state.chef,
      ...updatedChef
    };
    this.setState({ chef, openModal: '', addressIndex: -1 });
  };

  revertAvailability = () => {
    const chef = { ...this.state.chef };
    chef.chef_availabilities = this.chefAvailabilitiesSnapshot;
    chef.chef_time_offs = this.chefTimeOffsSnapshot;
    this.setState({ chef });
  };

  handleSubmit = () => {
    const chef = { ...this.state.chef };
    if (chef.id)
      chefAPI
        .updateChef(chef)
        .then(chef => {
          this.props.addMessage({
            className: 'success',
            message: `"${chef.name}" has been updated.`
          });
          this.setState({ chef, unsaved: false }); // redirect_to: '/admin/chefs',
        })
        .catch(error => {
          console.log(error);
          this.props.addMessage({
            className: 'error',
            message: `Error saving chef: ${error}`
          });
        });
  };

  loadChef(id) {
    chefAPI
      .getChef(id)
      .then(chef => {
        this.chefAvailabilitiesSnapshot = chef.chef_availabilities || [];
        this.chefTimeOffsSnapshot = chef.chef_time_offs || [];
        this.setState({ chef, isLoading: false });
      })
      .catch(this.handleErrorLoading);
  }

  handleDeclineService = () => {
    const { chef, opportunityIndex = -1 } = this.state;
    const { id: chefID = 0 } = chef;

    if (chefID > 0 && opportunityIndex >= 0) {
      const opportunity = chef.service_invited_chefs[opportunityIndex];

      chefAPI
        .declineService(chefID, opportunity.service.id)
        .then(response => {
          const service_invited_chefs = [
            ...chef.service_invited_chefs.slice(0, opportunityIndex),
            ...chef.service_invited_chefs.slice(
              opportunityIndex + 1,
              chef.service_invited_chefs.length
            )
          ];

          this.updateChef({ service_invited_chefs });
          this.props.addMessage({
            className: 'success',
            message: `Service has been declined.`
          });
        })
        .catch(err => {
          this.props.addMessage({
            className: 'error',
            message: stringfyServerErrors(err)
          });
        });
    }
  };

  handleResetPassword = () => {
    const { chef } = this.state;

    const LocalRedirectUrl = `${ENV_CONFIG.REACT_APP_SCHEMA}://${
      document.domain
    }/reset-password/chef`;

    const payload = {
      role: 'chef',
      email: chef.email,
      redirect_url: LocalRedirectUrl //`${ENV_CONFIG.REACT_APP_SCHEMA}://${ENV_CONFIG.REACT_APP_APP_HOST}/reset-password/chef`,
    };
    authAPI
      .forgotPassword(payload)
      .then(res => {
        this.props.addMessage({
          className: 'error',
          message:
            "We've just sent you an email with instructions on how to resend your password"
        });
        this.closeModal();
      })
      .catch(err => {
        this.props.addMessage({
          className: 'error',
          message: stringfyServerErrors(err)
        });
        this.closeModal();
      });
  };

  handleErrorLoading = error => {
    console.log(error);
    const pathname = '/';
    const redirect_to = { pathname };

    const { state } = this.props.location;
    if (state) {
      const { redirect_url, redirect_payload } = state;
      if (redirect_url) {
        redirect_to.pathname = redirect_url;
      }

      if (redirect_payload) redirect_to.state = { ...redirect_payload };
    }
    this.props.addMessage({
      className: 'error',
      message: stringfyServerErrors(error)
    }); // todo: add messaging
    this.setState({ redirect_to, isLoading: false });
  };

  maskAvailability(value) {
    return value ? 'Weekly & Occasion Services' : 'Weekly Services';
  }

  maskCompleted(value) {
    return ` (${value} completed)`;
  }

  componentDidMount() {
    chefAPI
      .getServiceDistricts()
      .then(({ service_districts: availableServiceDistricts }) => {
        this.setState({
          availableServiceDistricts,
          zipMap: createZipMap(availableServiceDistricts)
        });
      })
      .catch(err =>
        this.props.addMessage({
          className: 'error',
          message: stringfyServerErrors(err)
        })
      );

    // todo: remove this temporary logic
    // temporary dev logic: allow admin to load this page for any user
    const { id } = this.props.match.params;
    if (id && id > 0) {
      this.loadChef(id);
      return;
    }

    // this will be the default behavior
    const { user } = this.props;
    if (user.role === 'chef' && user.id > 0) this.loadChef(user.id);
    // else
    //   redirect
  }
}
export default withUser(withSplashMessage(withTracker(ChefProfile)));



// WEBPACK FOOTER //
// ./src/components/chef/ChefProfile.js